import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Loader2, FileText, Printer, RefreshCw, FileSpreadsheet, FileDown } from "lucide-react";
import { toast } from "sonner";
import { API, useAuth } from "../App";
import { DateInput } from "./ui/date-input";

/**
 * Δελτίο Έκδοσης Πράξεων Ημέρας
 * Εμφανίζει πίνακα πράξεων ανά αντικείμενο/τμήμα, ταξινομημένο κατά σειρά έκδοσης.
 * Στήλες: Αρ.Πράξης | ΓΑΚ/ΕΑΚ | Ημ.Ορισμού Δικαστή Προδ. | Αρ.Πράξης ΤΣΔ | Πρόεδρος Προσδ. | Δικαστής Προσδ. | Παρατηρήσεις
 */
const DailyActsBulletinTab = () => {
  const { user } = useAuth();
  const userCourtTypes = user?.court_types || ["Μονομελές", "Πολυμελές"];
  const defaultCt = userCourtTypes.length === 1 ? userCourtTypes[0] : "all";

  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [date, setDate] = useState(today);
  const [courtType, setCourtType] = useState(defaultCt);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const fetchBulletin = async () => {
    if (!date) {
      toast.error("Επιλέξτε ημερομηνία");
      return;
    }
    setLoading(true);
    try {
      const params = { date };
      if (courtType && courtType !== "all") params.court_type = courtType;
      const r = await axios.get(`${API}/pretrial-tools/daily-acts-bulletin`, { params });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Αποτυχία φόρτωσης δελτίου");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBulletin();
  }, []);

  const handlePrint = () => {
    window.print();
  };

  const handleExport = async (fmt) => {
    if (!date) {
      toast.error("Επιλέξτε ημερομηνία");
      return;
    }
    try {
      const params = new URLSearchParams({ date, format: fmt });
      if (courtType && courtType !== "all") params.append("court_type", courtType);
      const res = await axios.get(
        `${API}/pretrial-tools/daily-acts-bulletin/export?${params.toString()}`,
        { responseType: "blob" }
      );
      const blob = res.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `deltio_praxeon_${date}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Έγινε εξαγωγή ${fmt.toUpperCase()}`);
    } catch (e) {
      let msg = `Αποτυχία εξαγωγής ${fmt.toUpperCase()}`;
      try {
        const txt = await e.response?.data?.text?.();
        if (txt) {
          const j = JSON.parse(txt);
          msg = j.detail || msg;
        }
      } catch {
        /* ignore parse errors */
      }
      toast.error(msg);
    }
  };

  const totalRows = data?.total || 0;
  const groups = data?.groups || [];

  return (
    <div className="space-y-4">
      {/* Φίλτρα */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap items-end gap-3 print:hidden">
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Ημερομηνία Έκδοσης</Label>
              <DateInput
                
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-44"
                data-testid="daily-bulletin-date"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Δικαστήριο</Label>
              <Select value={courtType} onValueChange={setCourtType}>
                <SelectTrigger className="w-40" data-testid="daily-bulletin-court-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {userCourtTypes.length > 1 && <SelectItem value="all">Όλα</SelectItem>}
                  {userCourtTypes.map((ct) => (
                    <SelectItem key={ct} value={ct}>{ct}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={fetchBulletin} disabled={loading} data-testid="daily-bulletin-refresh">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Ενημέρωση
            </Button>
            <Button variant="outline" onClick={handlePrint} disabled={!totalRows} data-testid="daily-bulletin-print">
              <Printer className="w-4 h-4 mr-2" />
              Εκτύπωση
            </Button>
            <Button variant="outline" onClick={() => handleExport("xlsx")} disabled={!totalRows} data-testid="daily-bulletin-xlsx">
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Excel
            </Button>
            <Button variant="outline" onClick={() => handleExport("pdf")} disabled={!totalRows} data-testid="daily-bulletin-pdf">
              <FileDown className="w-4 h-4 mr-2" />
              PDF
            </Button>
            <Badge variant={totalRows ? "default" : "secondary"} className="h-9 px-3 flex items-center" data-testid="daily-bulletin-count">
              {totalRows} πράξεις
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Header εκτύπωσης */}
      <div className="hidden print:block mb-4">
        <h1 className="text-xl font-bold text-center uppercase">
          {(user?.court_branch || "Πρωτοδικείο Αθηνών")}
        </h1>
        <h2 className="text-lg font-semibold text-center mt-1">
          Δελτίο Έκδοσης Πράξεων — {formatGreekDate(date)}
        </h2>
        {courtType !== "all" && (
          <p className="text-center text-sm mt-1">Δικαστήριο: {courtType}</p>
        )}
      </div>

      {/* Αποτελέσματα */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      )}

      {!loading && totalRows === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-slate-500">
            <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="text-sm">Δεν εκδόθηκαν πράξεις στις {formatGreekDate(date)}{courtType !== "all" ? ` (${courtType})` : ""}.</p>
          </CardContent>
        </Card>
      )}

      {!loading && groups.map((g, gi) => (
        <Card key={`${g.court_type}-${g.sector_number}`} data-testid={`bulletin-group-${gi}`}>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-3 print:mb-2">
              <h3 className="text-base font-semibold text-slate-900">
                {g.court_type} — {g.sector_number}ο Τμήμα: {g.sector_title}
              </h3>
              <Badge variant="outline" className="print:hidden">{g.rows.length}</Badge>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-slate-50 print:bg-slate-100">
                    <th className="text-left p-2 border border-slate-200 font-semibold">Αρ. Πράξης</th>
                    <th className="text-left p-2 border border-slate-200 font-semibold">ΓΑΚ/ΕΑΚ/Έτος</th>
                    <th className="text-left p-2 border border-slate-200 font-semibold">Ημ. Ορισμού<br/>Δικαστή Προδ.</th>
                    <th className="text-left p-2 border border-slate-200 font-semibold">Αρ. Πράξης ΤΣΔ</th>
                    <th className="text-left p-2 border border-slate-200 font-semibold">Πρόεδρος Προσδιορισμού</th>
                    <th className="text-left p-2 border border-slate-200 font-semibold">Δικαστής Προσδιορισμού</th>
                    <th className="text-left p-2 border border-slate-200 font-semibold">Παρατηρήσεις</th>
                  </tr>
                </thead>
                <tbody>
                  {g.rows.map((r, ri) => (
                    <tr key={r.case_id || ri} className="hover:bg-slate-50 print:hover:bg-transparent" data-testid={`bulletin-row-${gi}-${ri}`}>
                      <td className="p-2 border border-slate-200 font-mono">{r.act_number}</td>
                      <td className="p-2 border border-slate-200 font-mono">{r.gak_eak}</td>
                      <td className="p-2 border border-slate-200">{formatGreekDate(r.judge_assignment_date)}</td>
                      <td className="p-2 border border-slate-200">{r.tsd_act_number}</td>
                      <td className="p-2 border border-slate-200">{r.designator_name}</td>
                      <td className="p-2 border border-slate-200">{r.assigned_judge}</td>
                      <td className="p-2 border border-slate-200 text-slate-500 italic">{r.notes || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

function formatGreekDate(dStr) {
  if (!dStr) return "—";
  try {
    const [y, m, d] = dStr.split("-");
    return `${d}/${m}/${y}`;
  } catch {
    return dStr;
  }
}

export default DailyActsBulletinTab;
